import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Timer, Trophy, Users, CheckCircle, XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface PlayerInterfaceProps {
  roomCode: string;
  onExit: () => void;
}

interface GameSession {
  id: number;
  game_name: string;
  room_code: string;
  question_ids: string;
  current_question_index: number;
  game_status: string;
  question_time_limit: number;
  question_started_at: string;
}

interface Question {
  id: number;
  question_text: string;
  answer_text: string;
  question_type: string;
  options?: string[];
}

interface Player {
  id: number;
  player_name: string;
  total_score: number;
}

const PlayerInterface: React.FC<PlayerInterfaceProps> = ({ roomCode, onExit }) => {
  const { toast } = useToast();
  const [gameSession, setGameSession] = useState<GameSession | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [playerName, setPlayerName] = useState('');
  const [playerId, setPlayerId] = useState<number | null>(null);
  const [isJoined, setIsJoined] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [hasAnswered, setHasAnswered] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState<Player[]>([]);
  const [gameStatus, setGameStatus] = useState<'joining' | 'waiting' | 'playing' | 'results' | 'completed'>('joining');

  useEffect(() => {
    if (isJoined) {
      const interval = setInterval(() => {
        updateGameState();
        updateLeaderboard();
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [isJoined]);

  useEffect(() => {
    if (gameSession?.game_status === 'active' && timeLeft > 0 && !hasAnswered) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => Math.max(0, prev - 1));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [gameSession?.game_status, timeLeft, hasAnswered]);

  const joinGame = async () => {
    if (!playerName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter your name',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Find game session
      const { data: gameData, error: gameError } = await window.ezsite.apis.tablePage(17213, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'room_code', op: 'Equal', value: roomCode }]
      });

      if (gameError) throw gameError;

      if (gameData.List.length === 0) {
        throw new Error('Game room not found');
      }

      const session = gameData.List[0];
      setGameSession(session);

      // Join as participant
      const playerUniqueId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const { error: joinError } = await window.ezsite.apis.tableCreate(17214, {
        game_session_id: session.id,
        player_name: playerName,
        player_id: playerUniqueId,
        total_score: 0,
        is_connected: true,
        joined_at: new Date().toISOString(),
        last_activity: new Date().toISOString()
      });

      if (joinError) throw joinError;

      // Get participant ID
      const { data: participantData, error: participantError } = await window.ezsite.apis.tablePage(17214, {
        PageNo: 1,
        PageSize: 1,
        OrderByField: 'id',
        IsAsc: false,
        Filters: [
        { name: 'game_session_id', op: 'Equal', value: session.id },
        { name: 'player_id', op: 'Equal', value: playerUniqueId }]

      });

      if (participantError) throw participantError;
      setPlayerId(participantData.List[0].id);
      setIsJoined(true);
      setGameStatus(session.game_status === 'waiting' ? 'waiting' : 'playing');

      if (session.game_status === 'active') {
        await loadCurrentQuestion(session);
      }

      toast({
        title: 'Joined Successfully!',
        description: `Welcome to ${session.game_name}`
      });
    } catch (error) {
      console.error('Error joining game:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to join game',
        variant: 'destructive'
      });
    }
  };

  const loadCurrentQuestion = async (session: GameSession) => {
    try {
      const questionIds = JSON.parse(session.question_ids);
      const currentQuestionId = questionIds[session.current_question_index];

      const { data: questionData, error: questionError } = await window.ezsite.apis.tablePage(16057, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'id', op: 'Equal', value: currentQuestionId }]
      });

      if (questionError) throw questionError;

      const question = questionData.List[0];
      let options = [];
      if (question.question_type === 'multiple_choice') {
        try {
          options = JSON.parse(question.answer_text);
        } catch {
          options = question.answer_text.split(',').map((opt: string) => opt.trim());
        }
      }

      setCurrentQuestion({ ...question, options });

      // Calculate time left
      const startTime = new Date(session.question_started_at).getTime();
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setTimeLeft(Math.max(0, session.question_time_limit - elapsed));

      setHasAnswered(false);
      setShowResult(false);
      setSelectedAnswer('');
    } catch (error) {
      console.error('Error loading question:', error);
    }
  };

  const updateGameState = async () => {
    if (!gameSession) return;

    try {
      const { data: gameData, error: gameError } = await window.ezsite.apis.tablePage(17213, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'id', op: 'Equal', value: gameSession.id }]
      });

      if (gameError) throw gameError;

      const updatedSession = gameData.List[0];

      if (updatedSession.game_status !== gameSession.game_status) {
        setGameSession(updatedSession);

        if (updatedSession.game_status === 'active') {
          setGameStatus('playing');
          await loadCurrentQuestion(updatedSession);
        } else if (updatedSession.game_status === 'completed') {
          setGameStatus('completed');
        }
      } else if (updatedSession.current_question_index !== gameSession.current_question_index) {
        setGameSession(updatedSession);
        await loadCurrentQuestion(updatedSession);
      }
    } catch (error) {
      console.error('Error updating game state:', error);
    }
  };

  const updateLeaderboard = async () => {
    if (!gameSession) return;

    try {
      const { data, error } = await window.ezsite.apis.tablePage(17214, {
        PageNo: 1,
        PageSize: 10,
        OrderByField: 'total_score',
        IsAsc: false,
        Filters: [{ name: 'game_session_id', op: 'Equal', value: gameSession.id }]
      });

      if (error) throw error;
      setLeaderboard(data.List);

      // Update current player's score
      const currentPlayer = data.List.find((p: any) => p.id === playerId);
      if (currentPlayer) {
        setScore(currentPlayer.total_score);
      }
    } catch (error) {
      console.error('Error updating leaderboard:', error);
    }
  };

  const submitAnswer = async (answer: string) => {
    if (!gameSession || !currentQuestion || !playerId || hasAnswered) return;

    setSelectedAnswer(answer);
    setHasAnswered(true);

    try {
      // Determine if answer is correct
      let correctAnswer = currentQuestion.answer_text;
      let isAnswerCorrect = false;

      if (currentQuestion.question_type === 'multiple_choice') {
        try {
          const options = JSON.parse(currentQuestion.answer_text);
          isAnswerCorrect = answer === options[0]; // Assuming first option is correct
        } catch {
          isAnswerCorrect = answer === correctAnswer;
        }
      } else {
        isAnswerCorrect = answer.toLowerCase() === correctAnswer.toLowerCase();
      }

      // Calculate points (faster answers get more points)
      const responseTime = gameSession.question_time_limit - timeLeft;
      const basePoints = 100;
      const timeBonus = Math.max(0, Math.floor(basePoints * (timeLeft / gameSession.question_time_limit)));
      const pointsEarned = isAnswerCorrect ? basePoints + timeBonus : 0;

      // Submit response
      const { error: responseError } = await window.ezsite.apis.tableCreate(17215, {
        game_session_id: gameSession.id,
        participant_id: playerId,
        question_id: currentQuestion.id,
        question_index: gameSession.current_question_index,
        selected_answer: answer,
        is_correct: isAnswerCorrect,
        points_earned: pointsEarned,
        response_time: responseTime,
        answered_at: new Date().toISOString()
      });

      if (responseError) throw responseError;

      // Update participant's total score
      const newTotalScore = score + pointsEarned;
      const { error: updateError } = await window.ezsite.apis.tableUpdate(17214, {
        id: playerId,
        total_score: newTotalScore,
        last_activity: new Date().toISOString()
      });

      if (updateError) throw updateError;

      setIsCorrect(isAnswerCorrect);
      setScore(newTotalScore);
      setShowResult(true);

      toast({
        title: isAnswerCorrect ? 'Correct!' : 'Incorrect',
        description: isAnswerCorrect ? `+${pointsEarned} points` : 'Better luck next time!',
        variant: isAnswerCorrect ? 'default' : 'destructive'
      });
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit answer',
        variant: 'destructive'
      });
    }
  };

  if (!isJoined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4" data-id="4i6kqp7fb" data-path="src/components/gameify/PlayerInterface.tsx">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md" data-id="aw68pxocr" data-path="src/components/gameify/PlayerInterface.tsx">

          <Card data-id="9c2fh55z4" data-path="src/components/gameify/PlayerInterface.tsx">
            <CardHeader data-id="cp1yr899e" data-path="src/components/gameify/PlayerInterface.tsx">
              <CardTitle className="text-center text-2xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="qlwmj00o4" data-path="src/components/gameify/PlayerInterface.tsx">
                Join Game
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6" data-id="2t9ztwie0" data-path="src/components/gameify/PlayerInterface.tsx">
              <div className="text-center" data-id="n68z992o1" data-path="src/components/gameify/PlayerInterface.tsx">
                <div className="text-3xl font-mono font-bold text-purple-600 mb-2" data-id="dky29p1vq" data-path="src/components/gameify/PlayerInterface.tsx">
                  {roomCode}
                </div>
                <p className="text-sm text-muted-foreground" data-id="4ui7gjxkl" data-path="src/components/gameify/PlayerInterface.tsx">Room Code</p>
              </div>

              <div data-id="lljsc5vd4" data-path="src/components/gameify/PlayerInterface.tsx">
                <Label htmlFor="player-name" data-id="op3ofwk29" data-path="src/components/gameify/PlayerInterface.tsx">Your Name</Label>
                <Input
                  id="player-name"
                  placeholder="Enter your name"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && joinGame()} data-id="p13ya7uxt" data-path="src/components/gameify/PlayerInterface.tsx" />

              </div>

              <div className="flex gap-2" data-id="6racuuba3" data-path="src/components/gameify/PlayerInterface.tsx">
                <Button variant="outline" onClick={onExit} className="flex-1" data-id="iby8vend4" data-path="src/components/gameify/PlayerInterface.tsx">
                  <ArrowLeft className="h-4 w-4 mr-2" data-id="2knpkelsq" data-path="src/components/gameify/PlayerInterface.tsx" />
                  Back
                </Button>
                <Button onClick={joinGame} className="flex-1" data-id="dhz5xfdle" data-path="src/components/gameify/PlayerInterface.tsx">
                  Join Game
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4" data-id="64anoxx6n" data-path="src/components/gameify/PlayerInterface.tsx">
      <div className="max-w-2xl mx-auto" data-id="pcd7drydu" data-path="src/components/gameify/PlayerInterface.tsx">
        {/* Header */}
        <div className="text-center mb-6" data-id="rss95972i" data-path="src/components/gameify/PlayerInterface.tsx">
          <h1 className="text-2xl font-bold text-purple-600" data-id="azw9qql8y" data-path="src/components/gameify/PlayerInterface.tsx">
            {gameSession?.game_name}
          </h1>
          <div className="flex items-center justify-center gap-4 mt-2" data-id="dp2oivt01" data-path="src/components/gameify/PlayerInterface.tsx">
            <Badge variant="outline" data-id="hck1ebkkk" data-path="src/components/gameify/PlayerInterface.tsx">
              <Users className="h-3 w-3 mr-1" data-id="ih7j2p49k" data-path="src/components/gameify/PlayerInterface.tsx" />
              {playerName}
            </Badge>
            <Badge variant="secondary" data-id="n4sllav26" data-path="src/components/gameify/PlayerInterface.tsx">
              <Trophy className="h-3 w-3 mr-1" data-id="pxj93k7u6" data-path="src/components/gameify/PlayerInterface.tsx" />
              {score} points
            </Badge>
          </div>
        </div>

        <AnimatePresence mode="wait" data-id="y8pmh67g9" data-path="src/components/gameify/PlayerInterface.tsx">
          {gameStatus === 'waiting' &&
          <motion.div
            key="waiting"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }} data-id="0203l42jx" data-path="src/components/gameify/PlayerInterface.tsx">

              <Card data-id="jhuk09k40" data-path="src/components/gameify/PlayerInterface.tsx">
                <CardContent className="p-8 text-center" data-id="bf92x056k" data-path="src/components/gameify/PlayerInterface.tsx">
                  <div className="animate-pulse" data-id="djv5lg3iz" data-path="src/components/gameify/PlayerInterface.tsx">
                    <Users className="h-16 w-16 mx-auto text-purple-600 mb-4" data-id="qt10irz16" data-path="src/components/gameify/PlayerInterface.tsx" />
                  </div>
                  <h2 className="text-xl font-bold mb-2" data-id="fxueqq0n2" data-path="src/components/gameify/PlayerInterface.tsx">Waiting for Game to Start</h2>
                  <p className="text-muted-foreground" data-id="ljap53qfn" data-path="src/components/gameify/PlayerInterface.tsx">
                    The host will start the game when ready.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          }

          {gameStatus === 'playing' && currentQuestion &&
          <motion.div
            key="playing"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6" data-id="9bzcuif3k" data-path="src/components/gameify/PlayerInterface.tsx">

              <Card data-id="3r76ryhcm" data-path="src/components/gameify/PlayerInterface.tsx">
                <CardHeader data-id="7yhzuz3ht" data-path="src/components/gameify/PlayerInterface.tsx">
                  <div className="flex items-center justify-between" data-id="ekz8mpmle" data-path="src/components/gameify/PlayerInterface.tsx">
                    <CardTitle className="text-lg" data-id="mkdbv3y4q" data-path="src/components/gameify/PlayerInterface.tsx">
                      Question {(gameSession?.current_question_index || 0) + 1}
                    </CardTitle>
                    <div className="flex items-center gap-2" data-id="o1h6kt5ih" data-path="src/components/gameify/PlayerInterface.tsx">
                      <Timer className="h-4 w-4" data-id="1emkeifg9" data-path="src/components/gameify/PlayerInterface.tsx" />
                      <span className="text-xl font-bold text-purple-600" data-id="1aee5mzzp" data-path="src/components/gameify/PlayerInterface.tsx">
                        {timeLeft}s
                      </span>
                    </div>
                  </div>
                  <Progress value={timeLeft / (gameSession?.question_time_limit || 30) * 100} data-id="fxfhw05bg" data-path="src/components/gameify/PlayerInterface.tsx" />
                </CardHeader>
                <CardContent data-id="0eitxad80" data-path="src/components/gameify/PlayerInterface.tsx">
                  <h2 className="text-xl font-bold mb-6 text-center" data-id="r3lllogkw" data-path="src/components/gameify/PlayerInterface.tsx">
                    {currentQuestion.question_text}
                  </h2>

                  {!hasAnswered &&
                <div className="space-y-3" data-id="7dagrwof3" data-path="src/components/gameify/PlayerInterface.tsx">
                      {currentQuestion.question_type === 'multiple_choice' && currentQuestion.options &&
                  <div className="grid gap-3" data-id="idtcab3sf" data-path="src/components/gameify/PlayerInterface.tsx">
                          {currentQuestion.options.map((option, index) =>
                    <Button
                      key={index}
                      variant="outline"
                      className="h-auto p-4 text-left justify-start"
                      onClick={() => submitAnswer(option)} data-id="ftmr02s9q" data-path="src/components/gameify/PlayerInterface.tsx">

                              <div className="flex items-center gap-3" data-id="67koku19c" data-path="src/components/gameify/PlayerInterface.tsx">
                                <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="rqhmn2drh" data-path="src/components/gameify/PlayerInterface.tsx">
                                  {String.fromCharCode(65 + index)}
                                </div>
                                <span data-id="69jw1tytc" data-path="src/components/gameify/PlayerInterface.tsx">{option}</span>
                              </div>
                            </Button>
                    )}
                        </div>
                  }

                      {currentQuestion.question_type === 'true_false' &&
                  <div className="grid grid-cols-2 gap-3" data-id="u4y5clr22" data-path="src/components/gameify/PlayerInterface.tsx">
                          <Button
                      variant="outline"
                      className="h-20 text-lg"
                      onClick={() => submitAnswer('True')} data-id="c6kx7yqig" data-path="src/components/gameify/PlayerInterface.tsx">

                            <div className="text-center" data-id="ascrtu9a2" data-path="src/components/gameify/PlayerInterface.tsx">
                              <div className="text-green-600 font-bold text-2xl" data-id="t958dby3q" data-path="src/components/gameify/PlayerInterface.tsx">T</div>
                              <div data-id="h9zf5gfk4" data-path="src/components/gameify/PlayerInterface.tsx">True</div>
                            </div>
                          </Button>
                          <Button
                      variant="outline"
                      className="h-20 text-lg"
                      onClick={() => submitAnswer('False')} data-id="1pmsmddpg" data-path="src/components/gameify/PlayerInterface.tsx">

                            <div className="text-center" data-id="p24e0e6sf" data-path="src/components/gameify/PlayerInterface.tsx">
                              <div className="text-red-600 font-bold text-2xl" data-id="5z64mwzi8" data-path="src/components/gameify/PlayerInterface.tsx">F</div>
                              <div data-id="iziycrxaw" data-path="src/components/gameify/PlayerInterface.tsx">False</div>
                            </div>
                          </Button>
                        </div>
                  }
                    </div>
                }

                  {hasAnswered && showResult &&
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center" data-id="vrk6fn7ha" data-path="src/components/gameify/PlayerInterface.tsx">

                      <div className={`inline-flex items-center gap-2 p-4 rounded-lg ${
                  isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`
                  } data-id="k3u2mr4n5" data-path="src/components/gameify/PlayerInterface.tsx">
                        {isCorrect ?
                    <CheckCircle className="h-6 w-6" data-id="3k9963kuv" data-path="src/components/gameify/PlayerInterface.tsx" /> :

                    <XCircle className="h-6 w-6" data-id="cf6xmx2vs" data-path="src/components/gameify/PlayerInterface.tsx" />
                    }
                        <span className="text-lg font-bold" data-id="uncepjtow" data-path="src/components/gameify/PlayerInterface.tsx">
                          {isCorrect ? 'Correct!' : 'Incorrect'}
                        </span>
                      </div>
                      <p className="mt-4 text-muted-foreground" data-id="1clj566xw" data-path="src/components/gameify/PlayerInterface.tsx">
                        Your answer: <span className="font-semibold" data-id="xza0q6arh" data-path="src/components/gameify/PlayerInterface.tsx">{selectedAnswer}</span>
                      </p>
                    </motion.div>
                }

                  {hasAnswered && !showResult &&
                <div className="text-center" data-id="e622vzm2o" data-path="src/components/gameify/PlayerInterface.tsx">
                      <div className="animate-pulse" data-id="xq3xsl36a" data-path="src/components/gameify/PlayerInterface.tsx">
                        <div className="text-lg font-medium" data-id="mvpph8km7" data-path="src/components/gameify/PlayerInterface.tsx">Answer submitted!</div>
                        <div className="text-muted-foreground" data-id="lkdu88rea" data-path="src/components/gameify/PlayerInterface.tsx">Waiting for results...</div>
                      </div>
                    </div>
                }
                </CardContent>
              </Card>
            </motion.div>
          }

          {gameStatus === 'completed' &&
          <motion.div
            key="completed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }} data-id="tb8gg8d40" data-path="src/components/gameify/PlayerInterface.tsx">

              <Card data-id="kuqaxqqkx" data-path="src/components/gameify/PlayerInterface.tsx">
                <CardContent className="p-8 text-center" data-id="hehdeagxn" data-path="src/components/gameify/PlayerInterface.tsx">
                  <Trophy className="h-16 w-16 mx-auto text-yellow-500 mb-4" data-id="xne8j54nz" data-path="src/components/gameify/PlayerInterface.tsx" />
                  <h2 className="text-2xl font-bold mb-2" data-id="zbrrgywlz" data-path="src/components/gameify/PlayerInterface.tsx">Game Completed!</h2>
                  <p className="text-lg mb-4" data-id="5ltffxp1k" data-path="src/components/gameify/PlayerInterface.tsx">
                    Your final score: <span className="font-bold text-purple-600" data-id="t7h8laq3o" data-path="src/components/gameify/PlayerInterface.tsx">{score} points</span>
                  </p>
                  
                  <div className="mt-6" data-id="10eqlvkgp" data-path="src/components/gameify/PlayerInterface.tsx">
                    <h3 className="text-lg font-semibold mb-4" data-id="wqng3cysd" data-path="src/components/gameify/PlayerInterface.tsx">Final Leaderboard</h3>
                    <div className="space-y-2" data-id="qrz7c5gqv" data-path="src/components/gameify/PlayerInterface.tsx">
                      {leaderboard.slice(0, 5).map((player, index) =>
                    <div
                      key={player.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                      player.id === playerId ? 'bg-purple-100 border-2 border-purple-300' : 'bg-gray-50'}`
                      } data-id="b8pt07k68" data-path="src/components/gameify/PlayerInterface.tsx">

                          <div className="flex items-center gap-3" data-id="eqt5mb186" data-path="src/components/gameify/PlayerInterface.tsx">
                            <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="uyqv1vt3s" data-path="src/components/gameify/PlayerInterface.tsx">
                              {index + 1}
                            </div>
                            <span className="font-medium" data-id="reuztc406" data-path="src/components/gameify/PlayerInterface.tsx">{player.player_name}</span>
                          </div>
                          <span className="font-bold" data-id="z1jc7evwt" data-path="src/components/gameify/PlayerInterface.tsx">{player.total_score} pts</span>
                        </div>
                    )}
                    </div>
                  </div>

                  <Button onClick={onExit} className="mt-6" data-id="hxzyq82b3" data-path="src/components/gameify/PlayerInterface.tsx">
                    Exit Game
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          }
        </AnimatePresence>
      </div>
    </div>);

};

export default PlayerInterface;